import { useState } from "react";
import ReactDOM from "react-dom";
import { 
  X, 
  Clock, 
  ChevronDown, 
  ChevronUp,
  PlayCircle,
  Dumbbell,
  Info,
  Trophy,
  Zap,
  ArrowLeft,
  Video
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { cn } from "../../lib/utils";
import ExerciseDetailModal from "../../components/ExerciseDetailModal";

// Helper to get technique display info
const getTechniqueDisplay = (tech) => {
  const techniques = {
    myo_reps: { icon: "⚡", color: "#F59E0B", name: "Myo-reps" },
    dropset: { icon: "📉", color: "#EF4444", name: "Drop-set" },
    isometric: { icon: "⏱️", color: "#3B82F6", name: "Isométrico" },
    topset: { icon: "🎯", color: "#8B5CF6", name: "Top set" },
    deadstop: { icon: "⏸️", color: "#EC4899", name: "Dead-stop" },
    partials: { icon: "↕️", color: "#14B8A6", name: "Parciales" },
    negatives: { icon: "➖", color: "#F97316", name: "Negativas" },
    zigzag: { icon: "🔄", color: "#06B6D4", name: "Zig-zag" },
    rm: { icon: "🏆", color: "#D4AF37", name: "RM" },
  };
  
  const techDef = techniques[tech.type];
  if (!techDef) return null;
  
  let label = techDef.name;
  const configValue = tech.config?.value;
  
  if (configValue) {
    switch (tech.type) {
      case "dropset":
        label = `Drop-set (${configValue} drops)`;
        break;
      case "isometric":
        label = `Isométrico ${configValue}s`;
        break;
      case "rm":
        label = `${configValue}RM`;
        break;
      case "myo_reps":
        label = `Myo-reps (${configValue})`;
        break;
      case "topset":
        label = `Top set (${configValue})`;
        break;
      default:
        break;
    }
  }
  
  return { ...techDef, label };
};

// Single Exercise Preview Card (Read-only)
const ExercisePreviewCard = ({ exercise, index, isExpanded, onToggle }) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Determinar si tiene video interno (no YouTube)
  const hasInternalVideo = exercise.video_url && 
    !exercise.video_url.includes("youtube") && 
    !exercise.video_url.includes("youtu.be");
  
  return (
    <>
      <Card className="bg-[#121212] border-[#2A2A2A]">
        <CardContent className="p-0">
          {/* Header - clickable to expand */}
          <button
            onClick={onToggle}
            className="w-full p-4 flex items-center gap-3 text-left"
            data-testid={`preview-exercise-${index}`}
          >
            {/* Exercise number */}
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-[#D4AF37]">{index + 1}</span>
            </div>
            
            {/* Exercise name and basic info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-base truncate">{exercise.name}</h3>
              <div className="flex items-center gap-3 text-sm mt-1">
                <span className="text-[#D4AF37] font-semibold">
                  {exercise.sets} × {exercise.reps}
                </span>
                <span className="flex items-center gap-1 text-[#666]">
                  <Clock className="w-3 h-3" />
                  {exercise.rest_seconds}s
                </span>
                {exercise.rir && (
                  <span className="text-[#888]">RIR {exercise.rir}</span>
                )}
              </div>
            </div>
            
            {/* Expand indicator */}
            <div className="flex-shrink-0 text-[#555]">
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </button>
        
        {/* Expanded details */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-0 border-t border-[#2A2A2A] space-y-3">
            {/* Intensity Techniques */}
            {exercise.techniques && exercise.techniques.length > 0 && (
              <div className="pt-3">
                <p className="text-[#666] text-xs uppercase tracking-wider mb-2">Técnicas de Intensidad</p>
                <div className="flex flex-wrap gap-2">
                  {exercise.techniques.map((tech, techIdx) => {
                    const techConfig = getTechniqueDisplay(tech);
                    if (!techConfig) return null;
                    return (
                      <span 
                        key={techIdx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{ 
                          backgroundColor: `${techConfig.color}15`, 
                          color: techConfig.color,
                          border: `1px solid ${techConfig.color}30`
                        }}
                      >
                        {techConfig.icon}
                        {techConfig.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Notes */}
            {exercise.notes && (
              <div className="pt-2">
                <p className="text-[#666] text-xs uppercase tracking-wider mb-1">Notas</p>
                <p className="text-[#888] text-sm">{exercise.notes}</p>
              </div>
            )}
            
            {/* RIR Explanation */}
            {exercise.rir && (
              <div className="pt-2">
                <p className="text-[#666] text-xs uppercase tracking-wider mb-1">Intensidad</p>
                <div className="bg-[#1A1A1A] rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white font-medium">RIR {exercise.rir}</span>
                  </div>
                  <p className="text-[#666] text-xs mt-1">
                    {exercise.rir === 0 ? "Llevar al fallo muscular" :
                     exercise.rir === 1 ? "Dejar 1 repetición en reserva" :
                     exercise.rir === 2 ? "Dejar 2 repeticiones en reserva" :
                     exercise.rir === 3 ? "Dejar 3 repeticiones en reserva" :
                     `Dejar ${exercise.rir} repeticiones en reserva`}
                  </p>
                </div>
              </div>
            )}
            
            {/* Video button - Opens internal modal instead of external link */}
            <div className="pt-2">
              <button
                onClick={() => setShowDetailModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg text-[#D4AF37] text-sm font-medium hover:bg-[#D4AF37]/30 transition-colors"
                data-testid={`preview-video-btn-${index}`}
              >
                <Video className="w-4 h-4" />
                Ver técnica
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    
    {/* Exercise Detail Modal */}
    {showDetailModal && (
      <ExerciseDetailModal
        exercise={{
          ...exercise,
          rest_seconds: exercise.rest_seconds || 60
        }}
        onClose={() => setShowDetailModal(false)}
      />
    )}
  </>
  );
};

// Superset Preview Block
const SupersetPreviewBlock = ({ exercises, startIndex, expandedExercises, onToggleExercise }) => {
  return (
    <div className="rounded-xl border-2 border-[#D4AF37]/40 bg-[#D4AF37]/5 overflow-hidden">
      {/* Superset header */}
      <div className="bg-[#D4AF37]/20 px-4 py-2 flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">
          Superserie - Sin descanso entre ejercicios
        </span>
      </div>
      
      {/* Exercises in superset */}
      <div className="p-2 space-y-2">
        {exercises.map((exercise, idx) => {
          const globalIndex = startIndex + idx;
          const isExpanded = expandedExercises.includes(exercise.id);
          
          return (
            <div key={exercise.id} className="relative">
              {/* Letter indicator for superset position */}
              <div className="absolute -left-1 top-4 w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center z-10">
                <span className="text-[#0B0B0B] text-xs font-bold">
                  {String.fromCharCode(65 + idx)}
                </span>
              </div>
              
              <div className="ml-4">
                <ExercisePreviewCard
                  exercise={exercise}
                  index={globalIndex}
                  isExpanded={isExpanded}
                  onToggle={() => onToggleExercise(exercise.id)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Workout Preview Component
const WorkoutPreview = ({ workout, program, onBack, onStartWorkout }) => {
  const [expandedExercises, setExpandedExercises] = useState([]);
  
  const toggleExercise = (exerciseId) => {
    setExpandedExercises(prev => 
      prev.includes(exerciseId) 
        ? prev.filter(id => id !== exerciseId)
        : [...prev, exerciseId]
    );
  };
  
  // Get day display name
  const getDayName = () => {
    if (workout?.name) return workout.name;
    return `Día ${workout?.code || ""}`;
  };
  
  // Group exercises by superset
  const getGroupedExercises = () => {
    if (!workout?.exercises) return [];
    
    const grouped = [];
    let currentSupersetGroup = null;
    let currentSuperset = [];
    let exerciseIndexCounter = 0;
    
    workout.exercises.forEach((exercise, idx) => {
      const ssGroup = exercise.superset_group;
      
      if (ssGroup) {
        if (currentSupersetGroup === ssGroup) {
          currentSuperset.push({ ...exercise, originalIndex: idx });
        } else {
          if (currentSuperset.length > 0) {
            grouped.push({
              type: 'superset',
              group: currentSupersetGroup,
              exercises: currentSuperset,
              startIndex: exerciseIndexCounter
            });
            exerciseIndexCounter += currentSuperset.length;
          }
          currentSupersetGroup = ssGroup;
          currentSuperset = [{ ...exercise, originalIndex: idx }];
        }
      } else {
        if (currentSuperset.length > 0) {
          grouped.push({
            type: 'superset',
            group: currentSupersetGroup,
            exercises: currentSuperset,
            startIndex: exerciseIndexCounter
          });
          exerciseIndexCounter += currentSuperset.length;
          currentSupersetGroup = null;
          currentSuperset = [];
        }
        grouped.push({
          type: 'single',
          exercise: { ...exercise, originalIndex: idx },
          index: exerciseIndexCounter
        });
        exerciseIndexCounter++;
      }
    });
    
    if (currentSuperset.length > 0) {
      grouped.push({
        type: 'superset',
        group: currentSupersetGroup,
        exercises: currentSuperset,
        startIndex: exerciseIndexCounter
      });
    }
    
    return grouped;
  };
  
  const groupedExercises = getGroupedExercises();
  
  // Render using Portal for full-screen
  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-[#0B0B0B] z-[9999] flex flex-col"
      style={{ 
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}
      data-testid="workout-preview-screen"
    >
      {/* Header */}
      <div className="flex-shrink-0 bg-[#0B0B0B] border-b border-[#2A2A2A]">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[#888] hover:text-white transition-colors"
            data-testid="preview-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Volver</span>
          </button>
          
          <div className="text-center flex-1">
            <h1 className="text-lg font-heading font-bold text-white">{getDayName()}</h1>
            <p className="text-xs text-[#666]">{program?.name || ""}</p>
          </div>
          
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
        
        {/* Stats bar */}
        <div className="flex items-center justify-center gap-6 px-4 pb-4">
          <div className="flex items-center gap-2">
            <Dumbbell className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-white font-semibold">{workout?.exercises?.length || 0}</span>
            <span className="text-[#666] text-sm">ejercicios</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#666] text-sm">~45-60 min</span>
          </div>
        </div>
        
        {/* Main CTA Button */}
        <div className="px-4 pb-4">
          <Button
            onClick={onStartWorkout}
            className="w-full h-14 bg-gradient-to-r from-[#D4AF37] to-orange-500 hover:from-[#E5C54D] hover:to-orange-400 text-[#0B0B0B] font-black uppercase tracking-wider text-base rounded-xl shadow-lg shadow-orange-500/30"
            data-testid="start-guided-workout-btn"
          >
            <PlayCircle className="w-6 h-6 mr-2" />
            EMPEZAR ESTE ENTRENAMIENTO
          </Button>
        </div>
      </div>
      
      {/* Exercise List - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-3 pb-8">
          {groupedExercises.map((item, groupIdx) => {
            if (item.type === 'superset') {
              return (
                <SupersetPreviewBlock
                  key={`superset-${item.group}-${groupIdx}`}
                  exercises={item.exercises}
                  startIndex={item.startIndex}
                  expandedExercises={expandedExercises}
                  onToggleExercise={toggleExercise}
                />
              );
            } else {
              const exercise = item.exercise;
              const isExpanded = expandedExercises.includes(exercise.id);
              
              return (
                <ExercisePreviewCard
                  key={exercise.id}
                  exercise={exercise}
                  index={item.index}
                  isExpanded={isExpanded}
                  onToggle={() => toggleExercise(exercise.id)}
                />
              );
            }
          })}
        </div>
      </div>
      
      {/* Bottom CTA (sticky) */}
      <div className="flex-shrink-0 p-4 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B] to-transparent border-t border-[#2A2A2A]">
        <Button
          onClick={onStartWorkout}
          className="w-full h-14 bg-gradient-to-r from-[#D4AF37] to-orange-500 hover:from-[#E5C54D] hover:to-orange-400 text-[#0B0B0B] font-black uppercase tracking-wider text-base rounded-xl shadow-lg shadow-orange-500/30"
          data-testid="start-guided-workout-bottom-btn"
        >
          <PlayCircle className="w-6 h-6 mr-2" />
          EMPEZAR ESTE ENTRENAMIENTO
        </Button>
      </div>
    </div>,
    document.body
  );
};

export default WorkoutPreview;
